import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/auth/hooks/useAuth';
import type { AuthUser } from '@/lib/auth-api';
import { api } from '@/lib/api';

// Accepts "devuser1".."devuser10", "dev@localhost" (= devuser1),
// "dev2@localhost".."dev10@localhost", or an exact email/username match.
// Exported for unit testing; not part of any public API.
export function resolveDevUserIndex(identifier: string, devUsers: AuthUser[]): number | null {
  const value = identifier.trim().toLowerCase();
  if (!value) return null;

  const userMatch = value.match(/^devuser(\d{1,2})$/);
  if (userMatch) {
    const n = parseInt(userMatch[1], 10);
    if (n >= 1 && n <= devUsers.length) return n - 1;
  }

  const emailMatch = value.match(/^dev(\d*)@localhost$/);
  if (emailMatch) {
    const n = emailMatch[1] === '' ? 1 : parseInt(emailMatch[1], 10);
    if (n >= 1 && n <= devUsers.length) return n - 1;
  }

  const directIndex = devUsers.findIndex(
    (u) => u.email.toLowerCase() === value || u.username?.toLowerCase() === value,
  );
  return directIndex >= 0 ? directIndex : null;
}

export function LoginPage() {
  const [identifier, setIdentifier] = useState(''); // email or username
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { signIn, isDevMode, devUsers, switchDevUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: string })?.from || '/';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (isDevMode) {
        const index = resolveDevUserIndex(identifier, devUsers);
        if (index === null) {
          throw new Error('Unknown dev user. Try devuser1, devuser2, … or dev@localhost.');
        }
        await switchDevUser(index);
        navigate(from, { replace: true });
        return;
      }

      // Check if identifier looks like an email
      const isEmail = identifier.includes('@');
      let emailToUse = identifier;

      if (!isEmail) {
        // Resolve username to email
        try {
          const response = await api.get<{ email: string }>('/auth/resolve-username', {
            params: { username: identifier },
          });
          emailToUse = response.data.email;
        } catch {
          // API returns 401 for invalid/not found usernames
          throw new Error('Invalid username or password');
        }
      }

      await signIn(emailToUse, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-linear-to-b from-background to-muted/30">
      {/* Main content - positioned above center */}
      <div className="flex-1 flex items-center justify-center px-4 pb-20">
        <div className="w-full max-w-sm">
          {/* Logo/Brand */}
          <div className="text-center mb-8">
            <img src="/fias-logo.png" alt="Fias" className="h-16 w-auto mx-auto mb-4 dark:invert" />
            <h1 className="text-2xl font-semibold text-foreground">
              Welcome back
              {isDevMode && (
                <span className="ml-2 align-middle text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-950/40 border border-amber-500/30 rounded px-1.5 py-0.5">
                  DEV
                </span>
              )}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {isDevMode
                ? 'Type a dev user (e.g. devuser1) — no password needed.'
                : 'Sign in to continue to Fias'}
            </p>
          </div>

          {/* Card */}
          <div className="bg-card rounded-xl border shadow-sm p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="identifier">{isDevMode ? 'Dev user' : 'Email or Username'}</Label>
                  <Input
                    id="identifier"
                    type="text"
                    placeholder={isDevMode ? 'devuser1' : 'you@example.com'}
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    required
                    autoComplete="username"
                    disabled={isLoading}
                    className="h-11"
                  />
                </div>

                {!isDevMode && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Password</Label>
                      <Link
                        to="/auth/forgot-password"
                        className="text-xs text-muted-foreground hover:text-primary transition-colors"
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <PasswordInput
                      id="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      disabled={isLoading}
                      className="h-11"
                    />
                  </div>
                )}
              </div>

              <Button type="submit" className="w-full h-11 mt-2" disabled={isLoading}>
                {isLoading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>
          </div>

          {/* Sign up link */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            Don't have an account?{' '}
            <Link to="/auth/signup" className="text-primary font-medium hover:underline">
              Sign up
            </Link>
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="py-4 text-center text-xs text-muted-foreground space-x-3">
        <Link to="/legal/terms" className="hover:text-foreground transition-colors">
          Terms of Service
        </Link>
        <span>·</span>
        <Link to="/legal/privacy" className="hover:text-foreground transition-colors">
          Privacy Policy
        </Link>
        <span>·</span>
        <Link to="/legal/acceptable-use" className="hover:text-foreground transition-colors">
          Acceptable Use Policy
        </Link>
      </div>
    </div>
  );
}
